import { Stack } from "@mui/material";
import { useEffect, useState } from "react";

import { inject } from "../../../../lib/itn-ui-kit/services/service-container";
import useService from "../../../hooks/useService";
import PaginationControlsV2 from "../../global/PaginationControlsV2";
import PermissionWrapper from "../../global/PermissionWrapper";

import AddButton from "./components/Buttons/AddButton";
import { AISummaryButton } from "./components/Buttons/AISummaryButton";
import SearchBar from "./components/Fields/SearchBar";
import SortBy from "./components/Fields/SortBy";
import NotesList from "./components/List/NotesList";
import AddEditModal from "./components/Modals/AddEditModal";
import ResponseModals from "./components/Modals/ResponseModals";
import useClientNotes from "./hooks/useClientNotes";
import useModalHandler from "./hooks/useModalHandler";
import { useNotePermissions } from "./hooks/useNotePermissions";

export default function ClientNotes({
  caseObj,
  customURLPath = null,
  ownerId = null,
  notesRepositoryKey = "repositories/case-notes-repository",
  ownerQueryKey = "case",
  includeOwnerInCreatePayload = true,
  hideNoteTypeSelector = false,
  notePermissionsHook = useNotePermissions,
  hideAISummary = false,
}) {
  const currentUserService = inject("current-user-service");
  const caseId = ownerId ?? caseObj?.pk ?? caseObj?.id;
  const handlers = useModalHandler();
  const userId = currentUserService?.user?.pk || currentUserService?.user?.id;

  const { canAddCaseNotes, canSeeAISummary } = notePermissionsHook();

  const {
    currentPage,
    data,
    handleCloseModal,
    handleOnClickAddBtn,
    handleOnClickEditBtn,
    handlePageChange,
    handlePinNote,
    handleSubmit,
    isLoading,
    mode,
    openModal,
    resultLimit,
    searchText,
    selectedNote,
    setSearchText,
    setSortValue,
    sortValue,
    totalCount,
  } = useClientNotes(caseId, handlers, customURLPath, {
    notesRepositoryKey,
    ownerQueryKey,
    includeOwnerInCreatePayload,
  });

  const aIRepository = useService("repositories/ai-call-summary-repository");

  const [unSeenCount, setUnseenCount] = useState();

  useEffect(() => {
    if (!canSeeAISummary || hideAISummary) {
      return;
    }
    try {
      const fetchUnSeenCount = async () => {
        const response = await aIRepository.query();
        setUnseenCount(response.unseenCount);
      };
      fetchUnSeenCount();
    } catch {
      console.error("Error fetching AI summary count");
      return;
    }
  }, [aIRepository, canSeeAISummary, hideAISummary]);

  const displayAISummary = !hideAISummary && unSeenCount > 0;

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex w-full flex-row justify-between gap-4">
        <SearchBar searchText={searchText} setSearchText={setSearchText} />
        <PermissionWrapper permission={canAddCaseNotes}>
          <AddButton handleAdd={handleOnClickAddBtn} />
        </PermissionWrapper>
      </div>
      <div className="mb-2 mt-6 flex w-full flex-row justify-start gap-4">
        <SortBy value={sortValue} setSortValue={setSortValue} />
        {displayAISummary && (
          <PermissionWrapper permission={canSeeAISummary}>
            <AISummaryButton unSeenCount={unSeenCount} />
          </PermissionWrapper>
        )}
      </div>
      <div className="relative flex flex-1 flex-col gap-2 overflow-y-auto">
        <NotesList
          data={data}
          handleEdit={handleOnClickEditBtn}
          handleOnClickPin={handlePinNote}
          isLoading={isLoading}
          notePermissionsHook={notePermissionsHook}
        />
      </div>
      {totalCount > resultLimit && (
        <div className="relative flex w-full justify-center">
          <Stack alignItems="center">
            <PaginationControlsV2
              currentPage={currentPage}
              handleChange={handlePageChange}
              resultCount={totalCount}
              resultLimit={resultLimit}
            />
          </Stack>
        </div>
      )}
      <AddEditModal
        mode={mode}
        openModal={openModal}
        handleCloseModal={handleCloseModal}
        data={selectedNote}
        handleSubmit={(noteId, reqData) => handleSubmit(noteId, mode, reqData)}
        hideNoteTypeSelector={hideNoteTypeSelector}
        userId={userId}
      />
      <ResponseModals
        mode={handlers.mode}
        modalSize={handlers.modalSize}
        additionalDetails={handlers.additionalDetails}
        openModal={handlers.openModal}
        handleCloseModal={handlers.handleCloseModal}
        showFailureModal={handlers.showFailureModal}
        handleFailure={handlers.handleFailure}
      />
    </div>
  );
}
